import { BREAKPOINT_ANALYSIS_SLUG, CAP_TABLE_SLUG, FUND_OWNERSHIP_SLUG } from 'common/constants/cap-table/capTable';
import { FINANCIAL_SLUG } from 'common/constants/financials/financials';
import { SUMMARY_SLUG } from 'common/constants/summary/summary';
import { VALUATIONS_SLUG } from 'common/constants/valuations/valuations';
import { WATERFALL_SLUG } from 'common/constants/waterfall';

export enum PdfExportFormat {
  DIGITAL = 'digital',
  A4 = 'a4',
  A3 = 'a3',
  LETTER = 'letter',
  LEGAL = 'legal',
}

export enum PdfExportOrientation {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape',
}

export const FORMAT_OPTIONS = [
  { value: PdfExportFormat.DIGITAL, label: 'Digital (Auto-fit)' },
  { value: PdfExportFormat.A4, label: 'A4' },
  { value: PdfExportFormat.A3, label: 'A3' },
  { value: PdfExportFormat.LETTER, label: 'Letter' },
  { value: PdfExportFormat.LEGAL, label: 'Legal' },
];

export interface PdfExportResult {
  success: boolean;
  error?: string;
  filename?: string;
  pdfInstance?: any; // JsPDF instance when returnPdfInMemory is true
}

export interface PdfExportOptions {
  /** The filename for the exported PDF (without extension) */
  filename?: string;
  /** Scale factor for the canvas (higher = better quality, larger file) */
  scale?: number;
  /** Background color for the PDF */
  backgroundColor?: string;
  /** PDF page format */
  format?: PdfExportFormat;
  /** PDF orientation */
  orientation?: PdfExportOrientation;
  /** Whether to use CORS for images */
  useCORS?: boolean;
  /** Whether to allow tainted canvas */
  allowTaint?: boolean;
  /** Custom width for the canvas */
  width?: number;
  /** Custom height for the canvas */
  height?: number;
  /** Quality of the image (0-1) */
  quality?: number;
  /** Whether to include scrollbars in the capture */
  includeScrollbars?: boolean;
  /** Whether to return PDF in memory instead of downloading immediately */
  returnPdfInMemory?: boolean;
}

export interface UsePdfExportOptions extends PdfExportOptions {
  /** Whether to show loading state during export */
  showLoading?: boolean;
  /** Callback when export starts */
  onExportStart?: () => void;
  /** Callback when export completes (success or failure) */
  onExportComplete?: (result: PdfExportResult) => void;
  /** Callback when export fails */
  onExportError?: (error: string) => void;
}

export interface MultiplePagesExportOptions extends PdfExportOptions {
  urls: string[];
  msDelayBetweenPages?: number;
  onProgress?: (currentPage: number, totalPages: number, percentage?: number) => void;
}

export interface MultiplePagesExportResult extends PdfExportResult {
  totalPages?: number;
  processedPages?: number;
}

export interface UsePdfExportReturn {
  /** Function to trigger PDF export for multiple elements using data-ref names */
  exportSinglePageAsPdf: (
    dataRefNames: string[],
    customOptions?: Partial<PdfExportOptions>
  ) => Promise<PdfExportResult>;
  /** Function to trigger multiple pages PDF export */
  exportMultiplePagesAsPdf: (options: MultiplePagesExportOptions) => Promise<MultiplePagesExportResult>;
  /** Loading state during export */
  isExporting: boolean;
  /** Loading state during multiple pages export */
  isMultiplePagesExporting: boolean;
  /** Last export result */
  lastExportResult: PdfExportResult | null;
  /** Last multiple pages export result */
  lastMultiplePagesExportResult: MultiplePagesExportResult | null;
}

// Constants to avoid magic numbers
export const CONVERSION_RATIOS = {
  /** Conversion ratio from millimeters to pixels at 96 DPI */
  MM_TO_PX: 3.779527559,
} as const;

export const PAGE_DIMENSIONS = {
  /** Standard Letter page dimensions in millimeters */
  LETTER_WIDTH_MM: 216,
  LETTER_HEIGHT_MM: 279,
} as const;

export const RENDERING_DELAYS = {
  /** Default rendering delay in milliseconds */
  DEFAULT_DELAY_MS: 100,
  /** Extended delay for Digital format rendering in milliseconds */
  DIGITAL_DELAY_MS: 500,
} as const;

export const SCALE_LIMITS = {
  /** Minimum scale factor for Digital format */
  DIGITAL_MIN_SCALE: 2,
  /** Maximum scale factor for Digital format */
  DIGITAL_MAX_SCALE: 4,
  /** Base width for scale calculation in pixels */
  BASE_SCALE_WIDTH_PX: 800,
} as const;

export const ELEMENT_SPACING = {
  /** Spacing between elements in pixels */
  ELEMENT_MARGIN_TOP_PX: 20,
  /** Padding for temporary container in pixels */
  TEMP_CONTAINER_PADDING_PX: 10,
} as const;

export const TEMP_CONTAINER_POSITION = {
  /** Off-screen position for temporary containers */
  OFF_SCREEN_LEFT: '-9999px',
  /** Starting vertical position */
  TOP_POSITION: '0',
} as const;

export const IMAGE_SETTINGS = {
  /** Maximum quality for image export (0-1 range) */
  MAX_QUALITY: 1,
  /** Image format for canvas export */
  PNG_FORMAT: 'png' as const,
} as const;

export const FILE_SETTINGS = {
  /** Default filename for PDF export */
  DEFAULT_FILENAME: 'pdf-report',
  /** Default background color */
  DEFAULT_BG_COLOR: 'white',
  /** Default PDF unit */
  PDF_UNIT: 'mm' as const,
} as const;

export const PDF_SUPPORTED_PAGES = [
  SUMMARY_SLUG,
  FINANCIAL_SLUG,
  CAP_TABLE_SLUG,
  FUND_OWNERSHIP_SLUG,
  BREAKPOINT_ANALYSIS_SLUG,
  VALUATIONS_SLUG,
  WATERFALL_SLUG,
];
